import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@rudi/api/react';
import { Button } from '@rudi/ui/button';
import {
  usePlatformVoiceWritingPolicy,
  useResetVoiceWritingPolicy,
  useSaveVoiceWritingPolicy,
  useVoiceWritingOptions,
  useVoiceWritingPolicy,
  useVoiceWritingProbe,
  useVoiceWritingSources,
} from '../api/hooks';
import type {
  VoiceWritingCeilings,
  VoiceWritingCredentialSource,
  VoiceWritingOptions,
  VoiceWritingPolicy,
  VoiceWritingPolicyRead,
  VoiceWritingProbe,
  VoiceWritingProbeRequest,
  VoiceWritingPurpose,
  VoiceWritingRetention,
  VoiceWritingRoute,
  VoiceWritingRoutePlan,
} from '../api/types';

const control =
  'mt-1 w-full min-w-0 rounded-3xl border border-input bg-background px-3 py-2 text-sm';
const purposes: Record<VoiceWritingPurpose, { label: string; kind: 'stt' | 'text' | 'tts' }> = {
  dictation_stt: { label: 'Dictation speech recognition', kind: 'stt' },
  consultation_stt: { label: 'Video consultation speech recognition', kind: 'stt' },
  meeting_stt: { label: 'Meeting speech recognition', kind: 'stt' },
  phone_stt: { label: 'Phone speech recognition', kind: 'stt' },
  dictation_cleanup: { label: 'Dictation cleanup', kind: 'text' },
  meeting_transform: { label: 'Meeting transformation', kind: 'text' },
  writing_improvement: { label: 'Improved writing suggestions', kind: 'text' },
  writing_check: { label: 'Spelling and grammar checks', kind: 'text' },
  phone_llm: { label: 'Phone LLM step', kind: 'text' },
  phone_tts: { label: 'Phone speech synthesis', kind: 'tts' },
};
// Only a Rudi that reports consultation_stt accepts it; an older one rejects the
// override as an unknown purpose, so the form leaves it out there.
export function supportsConsultationSpeech(saved: Pick<VoiceWritingPolicyRead, 'effective'>) {
  return !!saved.effective?.consultation_stt;
}
function shownPurposes(saved: VoiceWritingPolicyRead) {
  return (
    Object.entries(purposes) as [VoiceWritingPurpose, (typeof purposes)[VoiceWritingPurpose]][]
  ).filter(([purpose]) => purpose !== 'consultation_stt' || supportsConsultationSpeech(saved));
}

// Rudi refuses a Mistral speech route outside OpenRouter's EU domain, because the
// EU domain is the only way to keep Mistral's processing in the EU.
export const OPENROUTER_EU_TRANSCRIPTION_ENDPOINT =
  'https://eu.openrouter.ai/api/v1/audio/transcriptions';
export function isEuOnlySpeechModel(protocol: string, model: string) {
  return protocol === 'openrouter_transcription' && model.startsWith('mistralai/');
}

const retentionLabels: Record<keyof VoiceWritingRetention, string> = {
  dictation_audio: 'Dictation audio',
  meeting_media: 'Meeting media',
  dictation_text: 'Dictation text',
  transcripts: 'Transcripts',
  generated_notes: 'Generated notes',
};
const ceilingLabels: Record<keyof VoiceWritingCeilings, string> = {
  monthly_spend_usd: 'Monthly spending ceiling in USD',
  audio_minutes_per_day: 'Audio minutes per day',
  text_jobs_per_day: 'Text jobs per day',
  stored_media_bytes: 'Stored media bytes',
  dictation_seconds: 'Dictation duration in seconds',
  meeting_seconds: 'Meeting duration in seconds',
  meeting_bytes: 'Meeting file size in bytes',
};

const keySources: Record<VoiceWritingCredentialSource, string> = {
  cortex_tenant: 'Tenant key from Rudi settings',
  cortex_global: 'Global key from Cortex',
  secret_file: 'Deployment secret file',
  environment: 'Deployment environment variable',
  development: 'Development key',
};

function CredentialStatus({
  credentialRef,
  options,
  onSetKey,
}: {
  credentialRef: string;
  options: VoiceWritingOptions;
  onSetKey?: (keyName: string) => void;
}) {
  const credential = options.credentials?.find(item => item.credential_ref === credentialRef);
  if (!credential) return null;
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2 text-xs sm:col-span-2">
      <span className={credential.source ? 'text-muted-foreground' : 'text-destructive'}>
        {credential.source ? keySources[credential.source] : 'No key configured'}
      </span>
      {onSetKey && credentialRef.startsWith('platform/') && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onSetKey(credential.key_name)}
        >
          Set {credential.key_name} key
        </Button>
      )}
    </div>
  );
}

function newRoute(kind: 'stt' | 'text' | 'tts', options: VoiceWritingOptions): VoiceWritingRoute {
  const model = options.models.find(item => item.features.includes(kind));
  return {
    protocol:
      model?.protocol ??
      (kind === 'stt' ? 'deepgram' : kind === 'text' ? 'openai_text' : 'openai_speech'),
    model: model?.model ?? '',
    effort: model?.efforts.includes('medium') ? 'medium' : (model?.efforts[0] ?? null),
    endpoint: null,
    credential_ref: '',
    region: '',
  };
}

function RouteFields({
  title,
  kind,
  route,
  options,
  onChange,
  onSetKey,
}: {
  title: string;
  kind: 'stt' | 'text' | 'tts';
  route: VoiceWritingRoute;
  options: VoiceWritingOptions;
  onChange: (value: VoiceWritingRoute) => void;
  onSetKey?: (keyName: string) => void;
}) {
  const models = options.models.filter(item => item.features.includes(kind));
  const selected = models.find(
    item => item.protocol === route.protocol && item.model === route.model
  );
  const approvals = options.approvals.filter(item => item.credential_ref === route.credential_ref);
  const endpoints = [...new Set(approvals.flatMap(item => item.endpoints))];
  const regions = [...new Set(approvals.flatMap(item => item.regions))];
  return (
    <fieldset className="grid min-w-0 gap-3 rounded-3xl border p-3 sm:grid-cols-2">
      <legend className="px-1 text-sm font-medium">{title}</legend>
      {kind !== 'text' && selected && <SpeechCapabilities features={selected.features} />}

      <label className="min-w-0 text-sm">
        Model and protocol
        <select
          className={control}
          aria-label={`${title} model and protocol`}
          value={`${route.protocol}/${route.model}`}
          onChange={event => {
            const item = models.find(
              model => `${model.protocol}/${model.model}` === event.target.value
            );
            if (item)
              onChange({
                ...route,
                protocol: item.protocol,
                model: item.model,
                effort: item.efforts.includes('medium') ? 'medium' : (item.efforts[0] ?? null),
                ...(isEuOnlySpeechModel(item.protocol, item.model)
                  ? { endpoint: OPENROUTER_EU_TRANSCRIPTION_ENDPOINT, region: 'eu' }
                  : { endpoint: null }),
              });
          }}
        >
          {!selected && (
            <option value={`${route.protocol}/${route.model}`}>
              {route.model || 'Choose a model'}
            </option>
          )}
          {models.map(item => (
            <option key={`${item.protocol}/${item.model}`} value={`${item.protocol}/${item.model}`}>
              {item.model} · {item.protocol}
              {item.installed ? '' : ' · adapter unavailable'}
            </option>
          ))}
        </select>
      </label>
      <label className="text-sm">
        Reasoning effort
        <select
          className={control}
          aria-label={`${title} reasoning effort`}
          value={route.effort ?? ''}
          onChange={event => onChange({ ...route, effort: event.target.value || null })}
        >
          {(selected?.efforts ?? [null]).map(effort => (
            <option key={effort ?? ''} value={effort ?? ''}>
              {effort ?? 'Not applicable'}
            </option>
          ))}
        </select>
      </label>
      <label className="min-w-0 text-sm">
        Managed credential reference
        <select
          className={control}
          aria-label={`${title} credential reference`}
          value={route.credential_ref}
          onChange={event =>
            onChange({ ...route, credential_ref: event.target.value, endpoint: null, region: '' })
          }
        >
          <option value="">Choose a managed reference</option>
          {!options.approvals.some(item => item.credential_ref === route.credential_ref) &&
            route.credential_ref && (
              <option value={route.credential_ref}>
                {route.credential_ref} · approval unavailable
              </option>
            )}
          {[...new Set(options.approvals.map(item => item.credential_ref))].map(ref => (
            <option key={ref} value={ref}>
              {ref}
            </option>
          ))}
        </select>
      </label>
      <CredentialStatus
        credentialRef={route.credential_ref}
        options={options}
        onSetKey={onSetKey}
      />
      {route.protocol !== 'codex' && (
        <label className="min-w-0 text-sm">
          Approved endpoint
          <select
            className={control}
            aria-label={`${title} endpoint`}
            value={route.endpoint ?? ''}
            onChange={event => onChange({ ...route, endpoint: event.target.value || null })}
          >
            <option value="">Choose an approved endpoint</option>
            {route.endpoint && !endpoints.includes(route.endpoint) && (
              <option value={route.endpoint}>{route.endpoint} · approval unavailable</option>
            )}
            {endpoints.map(endpoint => (
              <option key={endpoint} value={endpoint}>
                {endpoint}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="text-sm">
        Processing region
        <select
          className={control}
          aria-label={`${title} region`}
          value={route.region}
          onChange={event => onChange({ ...route, region: event.target.value })}
        >
          <option value="">Choose an approved region</option>
          {route.region && !regions.includes(route.region) && (
            <option value={route.region}>{route.region} · approval unavailable</option>
          )}
          {regions.map(region => (
            <option key={region} value={region}>
              {region}
            </option>
          ))}
        </select>
      </label>
    </fieldset>
  );
}

function PlanFields({
  title,
  kind,
  plan,
  options,
  onChange,
  onSetKey,
}: {
  title: string;
  kind: 'stt' | 'text' | 'tts';
  plan: VoiceWritingRoutePlan | null;
  options: VoiceWritingOptions;
  onChange: (value: VoiceWritingRoutePlan | null) => void;
  onSetKey?: (keyName: string) => void;
}) {
  return (
    <div className="min-w-0 space-y-3">
      <label className="flex items-center gap-2 text-sm font-medium">
        <input
          type="checkbox"
          checked={!!plan}
          onChange={event =>
            onChange(
              event.target.checked ? { primary: newRoute(kind, options), fallbacks: [] } : null
            )
          }
        />
        {title}
      </label>
      {plan && (
        <>
          <RouteFields
            title={`${title} primary`}
            kind={kind}
            route={plan.primary}
            options={options}
            onChange={primary => onChange({ ...plan, primary })}
            onSetKey={onSetKey}
          />
          {plan.fallbacks.map((route, index) => (
            <div key={index} className="space-y-2">
              <RouteFields
                title={`${title} fallback ${index + 1}`}
                kind={kind}
                route={route}
                options={options}
                onChange={value =>
                  onChange({
                    ...plan,
                    fallbacks: plan.fallbacks.map((item, i) => (i === index ? value : item)),
                  })
                }
                onSetKey={onSetKey}
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={index === 0}
                  aria-label={`Move ${title} fallback ${index + 1} earlier`}
                  onClick={() => {
                    const fallbacks = [...plan.fallbacks];
                    [fallbacks[index - 1], fallbacks[index]] = [
                      fallbacks[index]!,
                      fallbacks[index - 1]!,
                    ];
                    onChange({ ...plan, fallbacks });
                  }}
                >
                  Move earlier
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  aria-label={`Remove ${title} fallback ${index + 1}`}
                  onClick={() =>
                    onChange({ ...plan, fallbacks: plan.fallbacks.filter((_, i) => i !== index) })
                  }
                >
                  Remove fallback
                </Button>
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={plan.fallbacks.length >= 5}
            onClick={() =>
              onChange({ ...plan, fallbacks: [...plan.fallbacks, newRoute(kind, options)] })
            }
          >
            Add {title.toLowerCase()} fallback
          </Button>
        </>
      )}
    </div>
  );
}

export function VoiceWritingPolicyForm({
  saved,
  options,
  pending,
  onSave,
  onProbe,
  onSetKey,
  scope = 'tenant',
}: {
  saved: VoiceWritingPolicyRead;
  options: VoiceWritingOptions;
  pending: boolean;
  onSave: (policy: VoiceWritingPolicy, expectedRevision: number) => Promise<void>;
  onProbe?: (payload: VoiceWritingProbeRequest) => Promise<VoiceWritingProbe>;
  onSetKey?: (keyName: string) => void;
  scope?: 'tenant' | 'platform';
}) {
  const [draft, setDraft] = useState(saved.policy);
  const [draftRevision, setDraftRevision] = useState(saved.revision);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  if (!draft || !saved.editable)
    return <p role="status">{saved.issues.join(' ') || 'This backend cannot edit this policy.'}</p>;
  const save = async (policy: VoiceWritingPolicy) => {
    setError(null);
    setMessage(null);
    for (const [key, label] of Object.entries(retentionLabels) as [
      keyof VoiceWritingRetention,
      string,
    ][]) {
      if (policy.retention[key] === null) {
        setError(`${label} retention is required`);
        return;
      }
    }
    try {
      await onSave(policy, draftRevision);
      setDraft(policy);
      setDraftRevision(draftRevision + 1);
      setMessage(
        scope === 'platform' ? 'Platform default saved.' : 'Voice and writing policy saved.'
      );
    } catch (err) {
      const data = (
        err as {
          response?: {
            data?: {
              error?: string;
              message?: string;
              detail?: string;
              details?: { errors?: string[] };
            };
          };
        }
      ).response?.data;
      setError(
        data?.details?.errors?.join(' ') ||
          data?.error ||
          data?.message ||
          data?.detail ||
          'Policy could not be saved. Reload and try again.'
      );
    }
  };
  return (
    <form
      className="space-y-5"
      onSubmit={event => {
        event.preventDefault();
        void save(draft);
      }}
    >
      <fieldset disabled={pending} className="min-w-0 space-y-5">
        {options.approvals.length === 0 && (
          <p role="status" className="text-sm text-muted-foreground">
            {scope === 'platform'
              ? 'No platform destinations are approved for this deployment.'
              : 'No managed destinations are approved for this tenant.'}
          </p>
        )}
        <label className="block text-sm">
          Approved processing regions, separated by commas
          <input
            className={control}
            value={draft.approved_regions.join(', ')}
            onChange={event =>
              setDraft({
                ...draft,
                approved_regions: event.target.value.split(',').map(value => value.trim()),
              })
            }
          />
        </label>
        <div className="grid min-w-0 gap-5 lg:grid-cols-2">
          <PlanFields
            title="Speech recognition default"
            kind="stt"
            plan={draft.stt_default}
            options={options}
            onChange={stt_default => setDraft({ ...draft, stt_default })}
            onSetKey={onSetKey}
          />
          <PlanFields
            title="Text processing default"
            kind="text"
            plan={draft.text_default}
            options={options}
            onChange={text_default => setDraft({ ...draft, text_default })}
            onSetKey={onSetKey}
          />
        </div>
        <PlanFields
          title="Phone speech synthesis"
          kind="tts"
          plan={draft.tts}
          options={options}
          onChange={tts => setDraft({ ...draft, tts })}
          onSetKey={onSetKey}
        />
        <fieldset className="min-w-0 space-y-4 rounded-3xl border p-4">
          <legend className="px-1 text-sm font-medium">Per-job overrides</legend>
          {shownPurposes(saved)
            .filter(([purpose]) => purpose !== 'phone_tts')
            .map(([purpose, { label, kind }]) => (
              <PlanFields
                key={purpose}
                title={label}
                kind={kind}
                plan={draft.overrides[purpose] ?? null}
                options={options}
                onChange={value => {
                  const overrides = { ...draft.overrides };
                  if (value) overrides[purpose] = value;
                  else delete overrides[purpose];
                  setDraft({ ...draft, overrides });
                }}
                onSetKey={onSetKey}
              />
            ))}
          <p className="text-xs text-muted-foreground">
            Jobs without an override inherit the matching {scope} default.
            {supportsConsultationSpeech(saved) &&
              ' Video consultations without an override and without a speech recognition default use the dictation override.'}{' '}
            Phone also requires a compatible Phone worker.
          </p>
        </fieldset>
        <fieldset className="rounded-3xl border p-4">
          <legend className="px-1 text-sm font-medium">Retention</legend>
          <p className="mb-3 text-xs text-muted-foreground">
            Set each category explicitly. Blank values block affected work. Zero deletes that
            category on completion. Users may delete earlier.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {(Object.entries(retentionLabels) as [keyof VoiceWritingRetention, string][]).map(
              ([key, label]) => (
                <label key={key} className="text-sm">
                  {label} retention in days
                  <input
                    className={control}
                    type="number"
                    min={0}
                    max={36500}
                    step={1}
                    value={draft.retention[key] ?? ''}
                    onChange={event =>
                      setDraft({
                        ...draft,
                        retention: {
                          ...draft.retention,
                          [key]: event.target.value === '' ? null : Number(event.target.value),
                        },
                      })
                    }
                  />
                </label>
              )
            )}
          </div>
        </fieldset>
        <fieldset className="rounded-3xl border p-4">
          <legend className="px-1 text-sm font-medium">Optional workload ceilings</legend>
          <p className="mb-3 text-xs text-muted-foreground">
            Existing spending controls still apply. Maximum capture limits are 600 seconds for
            dictation and 14,400 seconds or 2 GB for meetings.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {(Object.entries(ceilingLabels) as [keyof VoiceWritingCeilings, string][]).map(
              ([key, label]) => (
                <label key={key} className="text-sm">
                  {label}
                  <input
                    className={control}
                    type="number"
                    min={key.endsWith('seconds') || key === 'meeting_bytes' ? 1 : 0}
                    step={key === 'monthly_spend_usd' ? '0.01' : 1}
                    value={draft.ceilings[key] ?? ''}
                    onChange={event =>
                      setDraft({
                        ...draft,
                        ceilings: {
                          ...draft.ceilings,
                          [key]: event.target.value === '' ? null : Number(event.target.value),
                        },
                      })
                    }
                  />
                </label>
              )
            )}
          </div>
        </fieldset>
        <div className="flex flex-wrap gap-2">
          <Button type="submit">
            {scope === 'platform' ? 'Save platform default' : 'Save voice and writing policy'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setDraft(saved.policy);
              setDraftRevision(saved.revision);
              setError(null);
              setMessage(null);
            }}
          >
            Discard unsaved changes
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              void save({
                ...draft,
                stt_default: null,
                text_default: null,
                tts: null,
                overrides: {},
              })
            }
          >
            Reset processing routes
          </Button>
        </div>
      </fieldset>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      {message && (
        <p role="status" className="text-sm">
          {message}
        </p>
      )}
      <div className="space-y-3" aria-label="Saved processing readiness">
        <h3 className="text-sm font-medium">
          {scope === 'platform' ? 'Saved platform default' : 'Saved effective policy'}, revision{' '}
          {saved.revision}
        </h3>
        <p className="text-xs text-muted-foreground">
          Readiness reflects the saved configuration, not a tested provider connection. Save changes
          to refresh it. Active work keeps its pinned policy. Synthetic probes do not verify
          recognition quality, regional compliance or Phone calls.
        </p>
        {shownPurposes(saved).map(([purpose, { label }]) => {
          const effective = saved.effective[purpose];
          return (
            <div key={purpose} className="min-w-0 rounded-3xl border p-3 text-sm">
              <p className="font-medium">
                {label}: {effective?.ready ? 'Ready' : 'Unavailable'}
              </p>
              <p className="text-muted-foreground">
                {effective?.source === 'override'
                  ? purpose === 'consultation_stt' && !saved.policy?.overrides.consultation_stt
                    ? 'Dictation override, used while no speech recognition default is set'
                    : 'Job override'
                  : effective?.source === 'tts'
                    ? 'Separate TTS configuration'
                    : effective?.source === 'builtin'
                      ? 'Rudi built-in text route, used until a text default is set'
                      : `Inherited ${purposes[purpose].kind === 'stt' ? 'speech recognition' : 'text processing'} default`}
              </p>
              {effective?.plan && (
                <p className="break-words">
                  {[effective.plan.primary, ...effective.plan.fallbacks]
                    .map(
                      route =>
                        `${route.model} · ${route.protocol} · ${route.effort ?? 'no reasoning effort'} · ${route.region}`
                    )
                    .join(' → ')}
                </p>
              )}
              {effective?.plan &&
                purposes[purpose].kind !== 'text' &&
                [effective.plan.primary, ...effective.plan.fallbacks].map((route, index) => (
                  <SpeechProbe
                    key={`${saved.revision}/${purpose}/${index}`}
                    title={`${label} ${index === 0 ? 'primary' : `fallback ${index}`}`}
                    route={route}
                    features={
                      options.models.find(
                        item => item.protocol === route.protocol && item.model === route.model
                      )?.features ?? []
                    }
                    payload={{ expected_revision: saved.revision, purpose, route_index: index }}
                    onProbe={onProbe}
                  />
                ))}
              {effective?.issues.map(issue => (
                <p key={issue} className="text-xs text-muted-foreground">
                  {issue}
                </p>
              ))}
              {effective?.warnings?.map(warning => (
                <p key={warning} className="text-xs text-muted-foreground">
                  Warning: {warning}
                </p>
              ))}
            </div>
          );
        })}
      </div>
    </form>
  );
}

interface ProcessingSummary {
  states: {
    purpose: string;
    status: string;
    error_code: string | null;
    route_index?: number;
    count: number;
  }[];
  usage: {
    audio_minutes: number;
    text_jobs: number;
    stored_media_bytes: number;
    cost_usd: number;
    reserved_usd: number;
    uncertain_attempts: number;
  };
}
const processingReasons: Record<string, string> = {
  audio_limit: 'Daily audio allowance exhausted',
  text_job_limit: 'Daily writing allowance exhausted',
  storage_limit: 'Retained storage allowance exhausted',
  spend_limit: 'Spending allowance exhausted',
  pricing_unavailable: 'Processing prices unavailable',
  credential_denied: 'Provider credential rejected',
  credential_unavailable: 'Provider credential unavailable',
  approval_missing: 'Route approval missing',
  region_unapproved: 'Region approval missing',
  expired: 'Processing expired',
  access_revoked: 'Access revoked',
  offline_authority_revoked: 'Offline authority revoked',
  invalid_provider_result: 'Invalid provider result',
  provider_timeout: 'Provider timed out',
  unsupported_capability: 'Unsupported processing capability',
};
function TenantProcessingSummary({ tenantId }: { tenantId: string }) {
  const api = useApiClient();
  const processing = useQuery({
    queryKey: ['voice-writing-processing', tenantId],
    enabled: !!tenantId,
    refetchInterval: 5000,
    queryFn: async () =>
      (await api.get<ProcessingSummary>(`/cortex/tenants/${tenantId}/voice-writing/processing`))
        .data,
  });
  const data = processing.data;
  return (
    <div className="space-y-2 rounded-3xl border p-3 text-sm" aria-label="Processing activity">
      <h3 className="font-medium">Processing activity</h3>
      {processing.isLoading ? (
        <p role="status">Loading processing activity...</p>
      ) : !data?.usage || !data.states ? (
        <p role="status">Processing activity is unavailable on this backend.</p>
      ) : (
        <>
          <p>Audio today: {data.usage.audio_minutes} minutes</p>
          <p>Writing jobs today: {data.usage.text_jobs}</p>
          <p>Retained media: {data.usage.stored_media_bytes} bytes</p>
          <p>
            Cost this month: ${data.usage.cost_usd.toFixed(4)} · Reserved: $
            {data.usage.reserved_usd.toFixed(4)}
          </p>
          {data.usage.uncertain_attempts > 0 && (
            <p>
              {data.usage.uncertain_attempts}{' '}
              {data.usage.uncertain_attempts === 1 ? 'attempt' : 'attempts'} awaiting cost
              reconciliation
            </p>
          )}
          {!data.states.length ? (
            <p>No processing jobs in the last 24 hours.</p>
          ) : (
            <ul className="space-y-1">
              {data.states.map((state, index) => (
                <li key={`${state.purpose}:${state.status}:${index}`}>
                  <span>
                    {purposes[state.purpose as VoiceWritingPurpose]?.label ?? 'Processing'}
                  </span>
                  <p>
                    {state.count} {state.status}
                    {state.error_code
                      ? ` · ${processingReasons[state.error_code] ?? 'Processing stopped'}`
                      : ''}
                  </p>
                  {(state.route_index ?? 0) > 0 && <p>Approved fallback {state.route_index}</p>}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function PolicySourceNote({
  tenantId,
  saved,
}: {
  tenantId: string;
  saved: VoiceWritingPolicyRead;
}) {
  const platform = usePlatformVoiceWritingPolicy();
  const reset = useResetVoiceWritingPolicy(tenantId);
  const [error, setError] = useState<string | null>(null);
  if (!saved.source) return null;
  const text = {
    platform: `This tenant uses the platform default, revision ${saved.revision}. Saving here gives it its own policy instead.`,
    deployment:
      "This tenant uses the deployment's DeepInfra default. Saving here gives it its own policy instead.",
    development:
      'This tenant uses the development default. Saving here gives it its own policy instead.',
    none: 'Neither this tenant nor the platform has a policy yet. Dictation stays off until one is saved.',
    tenant: 'This tenant has its own policy, so platform default changes do not apply to it.',
  }[saved.source];
  return (
    <div role="status" className="space-y-2 rounded-3xl border p-3 text-sm">
      <p>{text}</p>
      {saved.source === 'tenant' && (platform.data?.revision ?? 0) > 0 && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={reset.isPending}
          onClick={() => {
            setError(null);
            reset
              .mutateAsync(saved.revision)
              .catch(() => setError('Could not switch to the platform default. Reload and retry.'));
          }}
        >
          Use platform default
        </Button>
      )}
      {error && (
        <p role="alert" className="text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

export function TenantVoiceWritingSection({ tenantId }: { tenantId: string }) {
  const policy = useVoiceWritingPolicy(tenantId);
  const options = useVoiceWritingOptions(tenantId);
  const save = useSaveVoiceWritingPolicy(tenantId);
  const probe = useVoiceWritingProbe(tenantId);
  const sources = useVoiceWritingSources(tenantId, policy.data?.revision);
  return (
    <section className="my-6 min-w-0 space-y-4 rounded-3xl border p-4">
      <div>
        <h2 className="text-sm font-medium">Voice and writing processing</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Configure tenant processing and retention. Only Cortex operators can change this policy.
        </p>
      </div>
      <TenantProcessingSummary tenantId={tenantId} />

      <div className="space-y-2 rounded-3xl border p-3">
        <h3>Phone and Video integration readiness</h3>
        <p className="text-sm">
          Saved settings do not activate Phone or Video. Phone keeps its EU Deepgram Nova-3, Mistral
          text, and Deepgram Aura-2 pipeline until a reviewed compatible worker supports
          alternatives.
        </p>
        {sources.isPending ? (
          <p role="status">Checking owning workers...</p>
        ) : sources.isError ? (
          <p role="status">Source readiness is unavailable on this backend.</p>
        ) : (
          sources.data.map(source => (
            <div key={source.source}>
              <p>
                {source.source === 'phone' ? 'Phone' : 'Video'}:{' '}
                {source.schema_version === 1 && source.ready ? 'Ready' : 'Unavailable'}
              </p>
              <p>
                Saved revision {source.saved_revision}. Worker revision{' '}
                {source.worker_revision ?? 'unavailable'}.
              </p>
              {source.issues.map(issue => (
                <p key={issue}>{issue}</p>
              ))}
            </div>
          ))
        )}
      </div>
      {policy.isLoading || options.isLoading ? (
        <p role="status">Loading processing policy...</p>
      ) : policy.error || options.error ? (
        <div role="status">
          <p>Voice and writing policy is unavailable on this backend.</p>
          <Button
            variant="outline"
            onClick={() => {
              void policy.refetch();
              void options.refetch();
            }}
          >
            Reload policy
          </Button>
        </div>
      ) : (
        policy.data &&
        options.data && (
          <>
            <PolicySourceNote tenantId={tenantId} saved={policy.data} />
            <VoiceWritingPolicyForm
              key={`${tenantId}/${policy.data.source ?? 'tenant'}`}
              saved={policy.data}
              options={options.data}
              pending={save.isPending}
              onProbe={payload => probe.mutateAsync(payload)}
              onSave={async (draft, expectedRevision) => {
                await save.mutateAsync({ expected_revision: expectedRevision, policy: draft });
              }}
            />
          </>
        )
      )}
    </section>
  );
}

function SpeechCapabilities({ features }: { features: string[] }) {
  return (
    <p className="text-xs text-muted-foreground sm:col-span-2">
      Timestamps: {features.includes('timestamps') ? 'Supported' : 'Unavailable'}. Speaker labels:{' '}
      {features.includes('speakers') ? 'Supported' : 'Unavailable'}. Vocabulary hints:{' '}
      {features.includes('dictionary') ? 'Supported' : 'Unavailable'}.
    </p>
  );
}

const probeErrors: Record<string, string> = {
  authentication_failed: 'Authentication failed',
  connection_failed: 'Provider connection failed',
  provider_rejected: 'Provider rejected the diagnostic',
  credential_unavailable: 'Speech credentials or media validation are unavailable',
  adapter_unavailable: 'Speech adapter is unavailable',
  invalid_route: 'Route lacks required capabilities, endpoint or region approval',
  invalid_route_or_response: 'Provider response is invalid',
};

function SpeechProbe({
  title,
  route,
  features,
  payload,
  onProbe,
}: {
  title: string;
  route: VoiceWritingRoute;
  features: string[];
  payload: VoiceWritingProbeRequest;
  onProbe?: (payload: VoiceWritingProbeRequest) => Promise<VoiceWritingProbe>;
}) {
  const [result, setResult] = useState<VoiceWritingProbe | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);
  return (
    <div className="mt-3 space-y-2 rounded-3xl border p-3" aria-label={`${title} diagnostic`}>
      <p>
        {title} · {route.model} · {route.region}
      </p>
      <SpeechCapabilities features={features} />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={pending || !onProbe}
        onClick={async () => {
          if (!onProbe) return;
          setPending(true);
          setError(false);
          setResult(null);
          try {
            setResult(await onProbe(payload));
          } catch {
            setError(true);
          } finally {
            setPending(false);
          }
        }}
      >
        Validate {title}
      </Button>
      <div role="status" aria-live="polite">
        {pending ? (
          'Running synthetic probe...'
        ) : error ? (
          'Diagnostic unavailable. Reload the saved policy and retry.'
        ) : result ? (
          <>
            <p>
              {result.status === 'passed'
                ? 'Synthetic probe passed'
                : (probeErrors[result.error_code ?? ''] ?? 'Synthetic probe failed')}
            </p>
            <p>
              Checked {result.checked_at} · {result.duration_ms} ms · revision {result.revision}
            </p>
            <p>{result.message}</p>
          </>
        ) : (
          'Connection not tested'
        )}
      </div>
    </div>
  );
}
